import {
    typeNumber,
    typeDataSource,
    typeXPath,
    typeBoolean,
} from '../constants/parameterTypes';
import {
    paramDsName,
    paramMaxLength,
    paramCtrlId,
    paramXPath,
    paramMoveNext,
    paramView,
} from '../constants/parameterNames';

const requiredParameters = [
    {
        name: paramMaxLength,
        description: 'The size limit for the control',
        type: typeNumber,
    },
];

const optionalParameters = [
    {
        name: paramXPath,
        description: `The XPath to the field(s) whose length should be limited. Either /${paramXPath} or /${paramCtrlId} should be specified, but not both. Should be the full path to the field without any brackets, double slashes, etc.`,
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramDsName,
        description:
            'The data source of the field to be limited. Defaults to the main data source if omitted',
        type: typeDataSource,
    },
    {
        name: paramCtrlId,
        description: 'The ctrlid (control id) of the control to limit.',
    },
    {
        name: paramView,
        description: `The view containing the control to limit. Defaults to all views if omitted. Must use /${paramXPath} (not /${paramCtrlId} if omitting /${paramView}`,
    },
    {
        name: paramMoveNext,
        description:
            '(boolean - defaults to false) When true, moves focus to the next control when the limit is reached.',
        type: typeBoolean,
    },
];

// Checks for:
// - paths that don't start with /
// - paths that contain //, [, ], ::, /., *, |
const invalidXPathCheck = /^[^/]|\/\/|\[|]|::|\/\.|\*|\|/;

const validateParams = (xpath, ctrlid, dsName, viewName) => {
    if (xpath && ctrlid) {
        return `/${paramXPath} and /${paramCtrlId} cannot be specified at the same time.`;
    }

    if (!(xpath || ctrlid)) {
        return `Must specify either /${paramXPath} or /${paramCtrlId}`;
    }

    if (dsName && !xpath) {
        return `Must specify /${paramXPath} if /${paramDsName} is specified.`;
    }

    if (!viewName && !xpath) {
        return `Must specify /${paramXPath} if /${paramView} is omitted.`;
    }

    if (xpath && invalidXPathCheck.test(xpath)) {
        return 'Invalid XPath. Please specify the full path to the field, including the leading slash, without //, [, ], ::, *, ., .., or |';
    }

    return null;
};

export const setCharacterLimitAction = (
    characterLimitSource,
    { getParamValue, getBoolParamValue, getIntParamValue },
) => {
    const xpath = getParamValue(paramXPath);
    const dsName = getParamValue(paramDsName);
    const ctrlid = getParamValue(paramCtrlId);
    const maxLength = getIntParamValue(paramMaxLength);
    const viewName = getParamValue(paramView);
    const moveNext = getBoolParamValue(paramMoveNext);

    const validationError = validateParams(xpath, ctrlid, dsName, viewName);

    if (validationError) {
        throw new Error(validationError);
    }

    characterLimitSource.addCustomLimit({
        viewName,
        ctrlid,
        xpath,
        maxLength,
        moveNext,
        dsName,
    });

    return '';
};

const SetCharacterLimit = ({ characterLimitSource, commonFunctions }) => ({
    description: 'Sets the character limit for view controls',
    requiredParameters,
    optionalParameters,
    execute: () =>
        setCharacterLimitAction(characterLimitSource, commonFunctions),
});

export default SetCharacterLimit;
